import { useMemo, useState } from 'react'
import { Link as RouterLink, useLocation } from 'react-router'

import CardActionArea from '@mui/material/CardActionArea'
import Snackbar from '@mui/material/Snackbar'
import Stack from '@mui/material/Stack'
import Typography from '@components/_mui/Typography'

import { Indicator, invalidateIndicator } from '@shared/hooks/src/useIndicator'
import usePubNub, { Channels } from '@shared/hooks/src/usePubNub'
import useUserSetting from '@shared/hooks/src/useUserSetting'
import { useMe } from '@shared/providers/src/MeProvider'
import { queryClient } from '@shared/providers/src/QueryClientProvider'
import { handleErrorSilently, NotificationSetting, QK, UserRole, UserSettingName } from '@shared/utils'

import useSound from '@hooks/useSound'
import Avatar from '@components/Avatar'

import { useGetPatient } from '../Appointments/Appointments.hooks'
import { styles, Title } from '../Notifications.utils'

/**
 * Handle the display of an Async Messaging Appointment notification.
 */
export default function AsyncMessagingAppointments() {
  const me = useMe()
  const notificationSettings = useUserSetting(me, UserSettingName.NotificationSettings)

  const [open, setOpen] = useState(false)
  const [patient, setPatient] = useState(undefined)
  const [message, setMessage] = useState(undefined)

  const currentPath = useLocation().pathname
  const isVisitPage = currentPath.startsWith('/app/visits')
  const isProvider = me.role === UserRole.Provider

  const enabled = useMemo(() => {
    if (isProvider && isVisitPage) return notificationSettings.value[NotificationSetting.AsyncMessagingAppointmentsDuringVisit] || false
    return true
  }, [isProvider, isVisitPage, notificationSettings.value])

  const handleClose = () => setOpen(false)

  const getPatient = useGetPatient()
  const playSound = useSound({ howlProps: { src: '/sounds/notification4.mp3' } })

  const handlePendingAsyncMessaging = (patientId) => {
    return getPatient
      .mutateAsync(patientId)
      .then((patient) => {
        setMessage('has just requested an async messaging appointment.')
        setPatient(patient)
        setOpen(true)
        playSound()
      })
      .catch(handleErrorSilently)
  }

  usePubNub(
    'platform_admins',
    ({ action, attributes }) => {
      if ([Channels.AsyncMessagingWaiting, Channels.AsyncMessagingAccepted].includes(action)) {
        invalidateIndicator(me.id, Indicator.HasOnDemandOrAsyncMessaging, { scope: 'accessible' })

        queryClient.invalidateQueries({ queryKey: QK.appointments.lists })
        queryClient.invalidateQueries({ queryKey: QK.appointments.asyncMessaging.lists })

        if (Channels.AsyncMessagingWaiting === action) {
          handlePendingAsyncMessaging(attributes.patient_id)
        }
      }
    },
    {
      enabled: me.role === UserRole.Admin,
    }
  )

  usePubNub(
    `user_${me.id}`,
    async ({ action, attributes }) => {
      const { appointment_id, patient_id } = attributes

      queryClient.invalidateQueries({ queryKey: QK.appointments.asyncMessaging.lists })

      if ([Channels.AsyncMessagingWaiting, Channels.AsyncMessagingAccepted].includes(action)) {
        invalidateIndicator(me.id, Indicator.HasOnDemandOrAsyncMessaging, { scope: 'accessible' })
        invalidateIndicator(me.id, Indicator.HasOnDemandOrAsyncMessaging, { scope: 'provider_queue' })
      }

      if (Channels.AsyncMessagingWaiting === action) {
        handlePendingAsyncMessaging(patient_id)
      }

      if (Channels.AsyncMessagingAccepted === action) {
        queryClient.invalidateQueries({ queryKey: QK.appointments.asyncMessaging.id(appointment_id).details })
      }
    },
    {
      enabled: me.role === UserRole.Provider && enabled,
    }
  )

  return (
    <Snackbar
      open={open}
      onClose={(event, reason) => {
        if (reason === 'escapeKeyDown') handleClose()
      }}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
    >
      <CardActionArea
        component={RouterLink}
        to="/app/appointments?tab=async-messaging"
        target="_blank"
        sx={[styles.container, styles.warning]}
      >
        <Title label="Async Messaging Appointments" onClose={handleClose} />
        <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
          <Avatar user={patient} size="sm" />
          <Typography>
            <b>{patient?.fullName}</b> {message}
          </Typography>
        </Stack>
      </CardActionArea>
    </Snackbar>
  )
}
